import {
  Building2,
  ClipboardCheck,
  ClipboardList,
  Compass,
  Cpu,
  FileSpreadsheet,
  GitBranch,
  Handshake,
  PackageCheck,
  ShieldCheck,
  Target,
  TrendingUp,
  Truck,
  Wrench,
  Beaker,
} from "lucide-react";
import { Link } from "react-router-dom";
import PiSymbol from "@/components/PiSymbol";

const SOP_STEPS = [
  { day: "Day N", title: "Enquiry Received", desc: "Buyer shares BOM, RFQ or part requirement." },
  { day: "Day N+3", title: "Pie Response", desc: "Acknowledgement, technical questions, scoping." },
  { day: "Day N+5", title: "Technical Feasibility & NDA", desc: "DFM review, signed NDA, capability matching." },
  { day: "Day N+7", title: "Quotation Submission", desc: "Should-cost backed quotation with full transparency." },
  { day: "Day N+14", title: "Negotiation & PO", desc: "Commercial alignment, PO release, kick-off meeting." },
  { day: "Stage 6", title: "Sample Development & Dispatch", desc: "Sample run, in-process inspection, dispatch." },
  { day: "Stage 7", title: "Sample Approval by Buyer", desc: "PPAP, master sample sign-off, tolerance approval." },
  { day: "Stage 8", title: "90% Payment / LC — Production Starts", desc: "Production launch, batch tracking begins." },
  { day: "Stage 9", title: "3rd Party Inspection", desc: "Independent NABL/3PI verification before dispatch." },
  { day: "Stage 10", title: "Packed & Shipped", desc: "Logistics-optimised packaging, OTIF dispatch, documentation." },
];

const TEAM_CARDS = [
  {
    role: "Cold Forging & Fastener Engineering",
    years: "40+ Years",
    industry: "Tier 1 Automotive, Heavy Engineering",
    expertise: "Cold forging process design, fastener metallurgy, DFM for high-precision hardware",
    icon: Wrench,
  },
  {
    role: "Rubber Technology — Compound Mixing",
    years: "31+ Years",
    industry: "Automotive Sealing, Industrial Rubber, FMCG",
    expertise: "Compound formulation, polymer selection, EPDM/NBR/Silicone processing",
    icon: Beaker,
  },
  {
    role: "Cold Forging & Value Engineering",
    years: "20+ Years",
    industry: "IT-enabled Manufacturing, Global Supply Chain",
    expertise: "Should costing, machining process optimisation, cross-category value engineering",
    icon: TrendingUp,
  },
  {
    role: "Rubber Compounding & Process Engineering",
    years: "31+ Years (Retd.)",
    industry: "Global Tier 1 Automotive OEM Supply",
    expertise: "Compound mixing, vulcanisation, process standardisation, IATF compliance",
    icon: Beaker,
  },
  {
    role: "Quality Assurance & Value Engineering",
    years: "10+ Years",
    industry: "Global Fastener Supply, Industrial Hardware",
    expertise: "PPAP, QPPM management, supplier audits, value engineering",
    icon: ShieldCheck,
  },
  {
    role: "Decals, Stickers & Adhesive Technology",
    years: "13+ Years",
    industry: "Luxury Automotive OEM Supply Chain",
    expertise: "Adhesive category management, decal engineering, premium-OEM compliance",
    icon: ClipboardCheck,
  },
  {
    role: "NPD — Polymers & Chemicals",
    years: "19+ Years • 3 Intl + 7 National Publications",
    industry: "Specialty Chemicals, Polymer Research",
    expertise: "NPD, polymer formulations, chemical compliance, material innovation",
    icon: Cpu,
  },
  {
    role: "Biobased & Sustainable Materials Research",
    years: "Research Scientist",
    industry: "Wageningen Food & Biobased Research, Netherlands",
    expertise: "Sustainable material alternatives, biopolymers, green procurement research",
    icon: Beaker,
  },
  {
    role: "Composite Materials & Innovation (Mentor)",
    years: "Head of Department",
    industry: "Composite Innovation Labs, India",
    expertise: "Advanced composites, materials mentoring, academic-industry collaboration",
    icon: Compass,
  },
];

export default function About() {
  return (
    <main data-testid="about-page" className="overflow-x-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 border-b border-white/5">
        <div className="absolute inset-0 grid-backdrop opacity-25 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <PiSymbol size={42} />
            <span className="section-label">About Pie Solutions</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05]">
            Not a trader. <br />
            <span className="text-gradient-green">A 360° system.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-white/75 text-base md:text-lg leading-relaxed">
            Pie Solutions (π) is a 360-degree Design-to-Deliver company for
            industrial components. We manage the entire procurement system —
            from sourcing to delivery — for manufacturing clients. Incubated at
            IIM Mumbai, with technology partners IIT Roorkee, CIPET, and Indo
            German Tool Room.
          </p>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section
        data-testid="vision-mission"
        className="relative py-24 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-card p-10">
            <div className="section-label mb-4">Our Vision</div>
            <Target size={28} className="text-[#39FF14] mb-4" />
            <p className="font-display text-2xl md:text-3xl font-semibold leading-snug">
              India's Leading Industrial Aggregator for Domestic & Cross-Border
              Export.
            </p>
          </div>
          <div className="glass-card p-10">
            <div className="section-label !text-[#00BFFF] mb-4 [&::before]:!bg-[#00BFFF]">
              Our Mission
            </div>
            <TrendingUp size={28} className="text-[#00BFFF] mb-4" />
            <p className="font-display text-2xl md:text-3xl font-semibold leading-snug">
              Achieving{" "}
              <span className="text-[#00BFFF]">₹300 Cr Revenue by 2027</span>{" "}
              through scalable, ethical aggregation.
            </p>
          </div>
        </div>
      </section>

      {/* B&C CLASS PROBLEM */}
      <section className="relative py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label !text-[#DC143C]">
              The B&C Class Problem
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              Why B&C Class parts are{" "}
              <span className="text-[#DC143C]">underserved</span>.
            </h2>
            <p className="text-white/75 mt-5 text-base md:text-lg leading-relaxed">
              B&C Class parts (low-value, high-frequency components like
              fasteners, rubber, decals, electronics commodities, and small
              moulded parts) sit at the long-tail of every BOM. They drive 80%
              of vendor count but only 20% of spend. Procurement teams ignore
              them. Suppliers under-serve them. And factories pay for it through
              line stoppages, dead inventory, and quality issues.
            </p>
            <p className="text-white/65 mt-5 text-base leading-relaxed">
              FIDM — our proprietary Fragment in Demand for Manufacturing
              framework — is built specifically to fix this. Read more on the{" "}
              <Link to="/" className="text-[#39FF14] underline">
                home page
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* SOP */}
      <section
        data-testid="sop-timeline"
        className="relative py-24 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Standard Operating Procedure</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              From enquiry to dispatch — in 10 measured steps.
            </h2>
          </div>
          <div className="mt-14 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
            <div className="space-y-8">
              {SOP_STEPS.map((s, i) => {
                const left = i % 2 === 0;
                return (
                  <div
                    key={s.title}
                    data-testid={`sop-step-${i + 1}`}
                    className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center"
                  >
                    <div
                      className={`md:order-${left ? "1" : "2"} ${
                        left ? "md:text-right" : ""
                      }`}
                    >
                      <div className="ml-10 md:ml-0 glass-card p-6 inline-block w-full md:w-auto md:max-w-md">
                        <div className="text-xs font-mono text-[#39FF14] mb-2">
                          {s.day}
                        </div>
                        <h3 className="font-display text-xl font-semibold">
                          {s.title}
                        </h3>
                        <p className="text-sm text-white/65 mt-2 leading-relaxed">
                          {s.desc}
                        </p>
                      </div>
                    </div>
                    <div className={`md:order-${left ? "2" : "1"}`} />
                    <div className="absolute left-4 md:left-1/2 top-6 -translate-x-1/2 w-4 h-4 rounded-full bg-[#0a0a0a] border-2 border-[#39FF14] shadow-[0_0_10px_rgba(57,255,20,0.5)]" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TEAM (no names) */}
      <section
        data-testid="expertise-section"
        className="relative py-24 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Our Expert Team</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              200+ combined years of engineering &{" "}
              <span className="text-[#39FF14]">procurement DNA</span>.
            </h2>
            <p className="text-white/70 mt-5 text-base md:text-lg">
              Across automotive, industrial, chemicals, and technology — our
              team is built for cross-category B&C aggregation, not single-line
              trading.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TEAM_CARDS.map((t, i) => (
              <div
                key={t.role}
                data-testid={`expertise-card-${i + 1}`}
                className="glass-card p-7"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center">
                    <t.icon size={20} className="text-[#39FF14]" />
                  </div>
                  <div className="font-mono text-[10px] text-white/35">
                    EXP / 0{i + 1}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-display mb-2">
                  {t.years}
                </div>
                <h3 className="font-display text-lg font-semibold mb-2 leading-snug">
                  {t.role}
                </h3>
                <div className="text-xs text-white/55 mb-3">
                  <span className="font-display uppercase tracking-[0.18em] mr-1">
                    Industry:
                  </span>
                  {t.industry}
                </div>
                <p className="text-sm text-white/75 leading-relaxed">
                  {t.expertise}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="border border-[#39FF14]/30 bg-[#39FF14]/5 rounded-xl p-7">
              <div className="font-display text-xl md:text-2xl font-semibold leading-snug">
                200+ Combined Years of Engineering & Procurement Expertise —
                across Automotive, Industrial, Chemical & Technology Sectors.
              </div>
            </div>
            <div className="border border-[#00BFFF]/30 bg-[#00BFFF]/5 rounded-xl p-7">
              <div className="font-display text-xl md:text-2xl font-semibold leading-snug">
                17-Member Dedicated Quality Team — backgrounds across Bharat
                Forge, Wheels India, Sundaram Rubber, Talbros, Sona Comstar,
                Mando, Rane, and Nippon.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUALITY FRAMEWORK */}
      <section
        data-testid="quality-framework"
        className="relative py-24 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Quality 1st Policy</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              Three pillars. Zero excuses.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              {
                tag: "New Part Development",
                icon: ClipboardList,
                items: [
                  "PPAP",
                  "Master Sample Sign-off",
                  "Tolerance Sign-off",
                  "Sample Submission",
                  "ECN/PCN",
                  "SEA",
                ],
              },
              {
                tag: "Production Quality",
                icon: PackageCheck,
                items: [
                  "PDIR",
                  "MTC",
                  "3rd Party Testing",
                  "QPPM",
                  "Process Audit",
                  "CAPA / 8D",
                ],
              },
              {
                tag: "Compliance & Warranty",
                icon: ShieldCheck,
                items: [
                  "Customer Rejection Management",
                  "Warranty Issues",
                  "ECN/PCN",
                  "Compliance Documentation",
                  "Warranty & Indemnity Bond",
                  "3-year Document Retention",
                ],
              },
            ].map((p) => (
              <div
                key={p.tag}
                data-testid={`quality-pillar-${p.tag.toLowerCase().replace(/\s+/g, "-")}`}
                className="glass-card p-7"
              >
                <p.icon size={26} className="text-[#39FF14] mb-4" />
                <div className="font-display text-xl font-semibold mb-5">
                  {p.tag}
                </div>
                <ul className="space-y-2">
                  {p.items.map((it) => (
                    <li
                      key={it}
                      className="text-sm text-white/75 flex gap-2"
                    >
                      <span className="text-[#39FF14] mt-1.5 inline-block w-1 h-1 rounded-full bg-[#39FF14] flex-shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECH DIFFERENTIATORS */}
      <section
        data-testid="tech-differentiators"
        className="relative py-24 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Technology Differentiators</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              Phygital procurement, end-to-end.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              {
                title: "Supplier Compass",
                desc: "60+ real-time data points for absolute supplier risk mapping. From IATF status to financial ratios — every node, scored.",
                icon: Compass,
              },
              {
                title: "Watch Tower",
                desc: "Supply Chain Watch Tower — transparent process, cost modelling, automated workflows, interactive dashboards.",
                icon: Building2,
              },
              {
                title: "Costing Framework",
                desc: "Market Analysis → Should Costing → SME Value Team → Development & Execution → Continuous Improvement.",
                icon: FileSpreadsheet,
              },
            ].map((t) => (
              <div
                key={t.title}
                data-testid={`tech-card-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="glass-card p-7"
              >
                <t.icon size={26} className="text-[#39FF14] mb-5" />
                <h3 className="font-display text-xl font-semibold mb-3">
                  {t.title}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">
                  {t.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Handshake size={40} className="text-[#39FF14] mx-auto" />
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-6">
            Let's design your supply chain together.
          </h2>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="btn-primary"
            >
              Talk to Pie
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
